import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  TextField,
  Container,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { CalendarToday, Lock, Logout } from '@mui/icons-material';

import { useAuth } from '@/hooks/useAuth';
import { useAppSelector, useAppDispatch } from '@/hooks/redux';
import { updateUser } from '@/store/authSlice';

const ForceChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/v1/user/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      if (response.ok) {
        dispatch(updateUser({ mustChangePassword: false }));
        toast.success('Password changed successfully');
        navigate('/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to change password');
      }
    } catch {
      toast.error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{
          m: 1,
          width: 56,
          height: 56,
          bgcolor: '#10b981'
        }}>
          <CalendarToday fontSize="large" />
        </Avatar>
        <Typography component="h1" variant="h4" gutterBottom>
          Player Central
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Change your password
        </Typography>

        <Card sx={{ mt: 3, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Lock color="primary" sx={{ fontSize: 64, mb: 2 }} />

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You must change your password before continuing.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                fullWidth
                helperText="Must be at least 8 characters"
              />
              <TextField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                startIcon={loading ? <CircularProgress size={20} /> : undefined}
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleLogout}
                startIcon={<Logout />}
              >
                Sign Out
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ForceChangePasswordPage;
